import React from 'react';

import classes from '../../../assets/Global.module.css';
import Input from '../../../assets/essentials/Input';
import Select from '../../../assets/essentials/Select';
import OptionList from '../../../helpers/options/OptionList';
import TextArea from '../../../assets/essentials/TextArea';
import StatesAndProvinces from '../../../helpers/options/StatesAndProvinces';
import GoogleSearchBoxCity from '../../../helpers/inputs/GoogleSearchBoxCity.js';
import { useUser } from '../../../hooks/useUser';

type DatDetailsProps = {
  draftDATPostParam: CreateDATTruckPostParam;
  createDATPostOptions: CreateDATTruckPostOptions;
  UpdateDraftDATPost: Function;
  setDatEquipmentTypeName: Function;
};

function Details({
  draftDATPostParam,
  createDATPostOptions,
  UpdateDraftDATPost,
  setDatEquipmentTypeName,
}: DatDetailsProps) {

  const { phoneExtension } = useUser()

  const OriginCallBackPlace = place => {
    UpdateDraftDATPost(null, 'originCity');
    UpdateDraftDATPost(null, 'originState');
    place.address_components.forEach(component => {
      component.types.forEach(type => {
        if (type === 'locality') {
          UpdateDraftDATPost(component.short_name, 'originCity');
        } else if (type === 'administrative_area_level_1') {
          UpdateDraftDATPost(component.short_name, 'originState');
        }
      });
    });
  };

  const DestinationCallBackPlace = place => {
    UpdateDraftDATPost(null, 'destinationCity');
    UpdateDraftDATPost(null, 'destinationState');
    place.address_components.forEach(component => {
      component.types.forEach(type => {
        if (type === 'locality') {
          UpdateDraftDATPost(component.short_name, 'destinationCity');
        } else if (type === 'administrative_area_level_1') {
          UpdateDraftDATPost(component.short_name, 'destinationState');
        }
      });
    });
    UpdateDraftDATPost(place.geometry.location.lat(), 'destinationLat');
    UpdateDraftDATPost(place.geometry.location.lng(), 'destinationLong');
  };

  return (
    <form className={classes.viewGrid}>
      <div className={`${classes.attribute}`}>
        <label htmlFor="equipmentType">
          Equipment Type <span className={classes.required}>*</span>
        </label>
        <Select
          id="equipmentType"
          name="datEquipmentCode"
          defaultValue={draftDATPostParam.datEquipmentCode || ''}
          onChange={e => {
            UpdateDraftDATPost(e.target.value, 'datEquipmentCode');
            setDatEquipmentTypeName(e.target.selectedOptions[0].textContent);
          }}
        >
          <option disabled value="">
            -- Required --
          </option>
          <OptionList
            optionData={createDATPostOptions?.datEquipmentTypeOptions}
            attributeID="datEquipmentCode"
            attributeName="datEquipmentTypeName"
          />
        </Select>
      </div>
      <div className={classes.attribute}>
        <label htmlFor="length">
          Length (ft)<span className={classes.required}>*</span>
        </label>
        <Select
          id="length"
          name="length"
          defaultValue={
            draftDATPostParam.length ? draftDATPostParam.length : ''
          }
          onChange={e => {
            UpdateDraftDATPost(e.target.value, 'length');
          }}
        >
          <option disabled value="">
            -- Required --
          </option>
          <OptionList
            optionData={createDATPostOptions?.lengthOptions}
            attributeID="intLength"
            attributeName="displayLength"
          />
        </Select>
      </div>
      <div className={`${classes.attribute}`}>
        <label htmlFor="weight">
          Weight (lbs) <span className={classes.required}>*</span>
        </label>
        <Input
          id="weight"
          name="weight"
          type="number"
          min="0"
          max="99999"
          defaultValue={draftDATPostParam.weight}
          onChange={e => {
            const value = e.target.value;
            if (value.length > 5) {
              e.target.value = value.slice(0, 5);
            }
            UpdateDraftDATPost(e.target.value, 'weight');
          }}
        />
      </div>
      <div className={`${classes.attribute}`}>
        <label htmlFor="origin">Origin Auto-Complete</label>
        <GoogleSearchBoxCity assignPlace={OriginCallBackPlace} id="origin" />
      </div>
      <div className={`${classes.attribute}`}>
        <label htmlFor="originCity">
          Origin City <span className={classes.required}>*</span>
        </label>
        <Input
          disabled
          id="originCity"
          name="originCity"
          value={draftDATPostParam.originCity}
        />
      </div>
      <div className={`${classes.attribute}`}>
        <label htmlFor="originState">
          Origin State/Province <span className={classes.required}>*</span>
        </label>
        <Select
          disabled
          id="originState"
          name="originState"
          value={draftDATPostParam.originState || ''}
        >
          <option disabled value=""></option>
          <StatesAndProvinces />
        </Select>
      </div>
      <div className={`${classes.attribute}`}>
        <label htmlFor="destination">Destination Auto-Complete</label>
        <GoogleSearchBoxCity
          assignPlace={DestinationCallBackPlace}
          id="destination"
        />
      </div>
      <div className={`${classes.attribute}`}>
        <label htmlFor="destinationCity">Destination City</label>
        <Input
          disabled
          id="destinationCity"
          name="destinationCity"
          value={draftDATPostParam.destinationCity}
        />
      </div>
      <div className={`${classes.attribute}`}>
        <label htmlFor="destinationState">Destination State/Province</label>
        <Select
          disabled
          id="destinationState"
          name="destinationState"
          value={draftDATPostParam.destinationState || ''}
        >
          <option disabled value=""></option>
          <StatesAndProvinces />
        </Select>
      </div>
      <div className={`${classes.attribute}`}>
        <label htmlFor="earliestAvailableDate">
          Earliest Availability <span className={classes.required}>*</span>
        </label>
        <Input
          id="earliestAvailableDate"
          name="earliestAvailableDate"
          type="date"
          defaultValue={draftDATPostParam.earliestAvailableDate}
          min={new Date().toISOString().split('T')[0]}
          onChange={e => {
            UpdateDraftDATPost(e.target.value, 'earliestAvailableDate');
          }}
        />
      </div>
      <div className={`${classes.attribute}`}>
        <label htmlFor="latestAvailableDate">
          Latest Availability <span className={classes.required}>*</span>
        </label>
        <Input
          id="latestAvailableDate"
          name="latestAvailableDate"
          type="date"
          defaultValue={draftDATPostParam.latestAvailableDate}
          min={draftDATPostParam.earliestAvailableDate}
          onChange={e => {
            UpdateDraftDATPost(e.target.value, 'latestAvailableDate');
          }}
        />
      </div>
      <div className={`${classes.attribute} ${classes.span3}`}>
        <label htmlFor="comment">
          Comment (
          {draftDATPostParam.comment
            ? 70 - draftDATPostParam.comment.length
            : 70}{' '}
          characters remaining)
        </label>
        <TextArea
          id="comment"
          name="comment"
          rows={1}
          defaultValue={phoneExtension ? "Ext: " + phoneExtension : ''}
          maxLength="70"
          onChange={e => UpdateDraftDATPost(e.target.value, 'comment')}
        />
      </div>
    </form>
  );
}
export default Details;
